import { useState, useEffect } from 'react';
import { getSiteSettings, updateSiteSettings, getShippingSettings, updateShippingSettings } from '../../firebase/database';
import { Spinner } from '../../components/common/Loader';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const PAKISTAN_CITIES_SHORT = ['Karachi','Lahore','Islamabad','Rawalpindi','Faisalabad','Multan','Peshawar','Quetta','Sialkot','Hyderabad','Gujranwala','Other'];

const AdminSettings = () => {
  const [site, setSite] = useState({ siteName:'SitSmart', siteTagline:'Smart Seating For Every Home', supportEmail:'info@sitsmart.store', supportPhone:'+92 333 1420200', address:'Karachi, Sindh, Pakistan', freeShippingThreshold:0 });
  const [shipping, setShipping] = useState({ rules:[], freeShippingThreshold:0, paymentAccounts:{ jazzcash:{number:'',name:''}, easypaisa:{number:'',name:''}, bank:{bankName:'',accountNo:'',accountTitle:'',iban:''} } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('general');

  useEffect(() => {
    Promise.all([getSiteSettings(), getShippingSettings()]).then(([s, sh]) => {
      if (s && Object.keys(s).length) setSite(prev => ({ ...prev, ...s }));
      if (sh) setShipping(sh);
    }).finally(() => setLoading(false));
  }, []);

  const handleSaveSite = async () => {
    setSaving(true);
    try { await updateSiteSettings(site); toast.success('Settings saved!'); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const handleSaveShipping = async () => {
    setSaving(true);
    try { await updateShippingSettings(shipping); toast.success('Shipping saved!'); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const addRule = () => setShipping(s => ({
    ...s, rules: [...(s.rules||[]), { id:`r_${Date.now()}`, city:'Karachi', minQty:1, maxQty:2, price:250, label:'', codAllowed:true }]
  }));

  const removeRule = (i) => setShipping(s => ({ ...s, rules: s.rules.filter((_,j) => j!==i) }));

  const updateRule = (i, field, val) => setShipping(s => ({
    ...s, rules: s.rules.map((r,j) => j===i ? { ...r, [field]: val } : r)
  }));

  const updatePayAcc = (type, field, val) => setShipping(s => ({
    ...s, paymentAccounts: { ...s.paymentAccounts, [type]: { ...s.paymentAccounts?.[type], [field]: val } }
  }));

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg"/></div>;

  return (
    <div>
      <div className="mb-6"><h1 className="font-display text-2xl font-black text-gray-900">Settings</h1></div>
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[['general','General'],['shipping','Shipping & Payment']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${tab===id?'border-[#1a3a6e] text-[#1a3a6e]':'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="bg-white rounded-2xl shadow-sm p-6 max-w-2xl space-y-4">
          {[
            { label:'Site Name', key:'siteName' },
            { label:'Tagline', key:'siteTagline' },
            { label:'Support Email', key:'supportEmail' },
            { label:'Support Phone', key:'supportPhone' },
            { label:'Address', key:'address' },
          ].map(({ label, key }) => (
            <div key={key}>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">{label}</label>
              <input value={site[key]||''} onChange={e => setSite(s => ({ ...s, [key]: e.target.value }))} className="input-field"/>
            </div>
          ))}
          <button onClick={handleSaveSite} disabled={saving} className="btn-primary text-sm flex items-center gap-2">
            {saving && <Spinner size="sm" color="white"/>} Save Settings
          </button>
        </div>
      )}

      {tab === 'shipping' && (
        <div className="space-y-6 max-w-3xl">
          {/* Shipping Rules */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-bold text-gray-900">Shipping Rules</h3>
                <p className="text-xs text-gray-400 mt-0.5">Set delivery charges by city & quantity. COD = Cash on Delivery allowed.</p>
              </div>
              <button onClick={addRule} className="btn-primary text-xs py-2 px-4 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
                Add Rule
              </button>
            </div>

            {/* Shipping rules table — scrolls horizontally on small screens */}
            <div className="overflow-x-auto -mx-2 px-2 mt-4">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-2 py-2 bg-gray-50 rounded-lg mb-2 text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[600px]">
              <div className="col-span-2">City</div>
              <div className="col-span-2">Min Qty</div>
              <div className="col-span-2">Max Qty</div>
              <div className="col-span-2">Price (PKR)</div>
              <div className="col-span-2">COD</div>
              <div className="col-span-1">Label</div>
              <div className="col-span-1"></div>
            </div>

            <div className="space-y-2 min-w-[600px]">
              {(shipping.rules||[]).map((rule, i) => (
                <motion.div key={rule.id||i} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                  className="grid grid-cols-12 gap-2 items-center p-2 border border-gray-100 rounded-xl hover:border-gray-200 transition-colors">
                  <div className="col-span-2">
                    <select value={rule.city} onChange={e => updateRule(i,'city',e.target.value)}
                      className="input-field text-xs py-2 px-2">
                      {PAKISTAN_CITIES_SHORT.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input type="number" min="1" value={rule.minQty} onChange={e => updateRule(i,'minQty',+e.target.value)}
                      className="input-field text-xs py-2 px-2" placeholder="1"/>
                  </div>
                  <div className="col-span-2">
                    <input type="number" min="1" value={rule.maxQty} onChange={e => updateRule(i,'maxQty',+e.target.value)}
                      className="input-field text-xs py-2 px-2" placeholder="999"/>
                  </div>
                  <div className="col-span-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 font-bold">PKR</span>
                      <input type="number" value={rule.price} onChange={e => updateRule(i,'price',+e.target.value)}
                        className="input-field text-xs py-2 pl-8 pr-2" placeholder="250"/>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <div onClick={() => updateRule(i,'codAllowed',!rule.codAllowed)}
                      className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors cursor-pointer ${rule.codAllowed?'bg-green-500':'bg-gray-300'}`}>
                      <motion.div animate={{ x: rule.codAllowed ? 20 : 0 }} transition={{ type:'spring', stiffness:500 }}
                        className="w-4 h-4 bg-white rounded-full shadow"/>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <input value={rule.label||''} onChange={e => updateRule(i,'label',e.target.value)}
                      className="input-field text-xs py-2 px-2" placeholder="Label"/>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button onClick={() => removeRule(i)} className="text-red-400 hover:text-red-600 transition-colors p-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>
                  </div>
                </motion.div>
              ))}
              {(!shipping.rules || shipping.rules.length === 0) && (
                <div className="text-center py-8 text-gray-400 text-sm">No rules yet. Add your first shipping rule above.</div>
              )}
            </div>

            {/* Example hint */}
            <div className="mt-4 bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-xs text-blue-700 font-semibold mb-1">Example Setup:</p>
              <p className="text-xs text-blue-600">Karachi 1-2 pcs → PKR 200, COD ✓ | Karachi 3+ pcs → PKR 300, COD ✗ (advance) | Other Cities 1-2 pcs → PKR 350, COD ✗ | Other Cities 3+ pcs → PKR 500, COD ✗</p>
            </div>
            </div>{/* end overflow-x-auto */}
          </div>

          {/* Payment Accounts */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-bold text-gray-900 mb-4">Payment Account Details</h3>
            <div className="space-y-5">
              {/* JazzCash */}
              <div>
                <p className="text-xs font-black text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#e63946] rounded-full"></span>JazzCash
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><label className="block text-xs text-gray-500 mb-1">Account Number</label>
                    <input value={shipping.paymentAccounts?.jazzcash?.number||''} onChange={e => updatePayAcc('jazzcash','number',e.target.value)} className="input-field text-sm" placeholder="0300-0000000"/></div>
                  <div><label className="block text-xs text-gray-500 mb-1">Account Name</label>
                    <input value={shipping.paymentAccounts?.jazzcash?.name||''} onChange={e => updatePayAcc('jazzcash','name',e.target.value)} className="input-field text-sm" placeholder="Account holder name"/></div>
                </div>
              </div>
              {/* EasyPaisa */}
              <div>
                <p className="text-xs font-black text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>EasyPaisa
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><label className="block text-xs text-gray-500 mb-1">Account Number</label>
                    <input value={shipping.paymentAccounts?.easypaisa?.number||''} onChange={e => updatePayAcc('easypaisa','number',e.target.value)} className="input-field text-sm" placeholder="0300-0000000"/></div>
                  <div><label className="block text-xs text-gray-500 mb-1">Account Name</label>
                    <input value={shipping.paymentAccounts?.easypaisa?.name||''} onChange={e => updatePayAcc('easypaisa','name',e.target.value)} className="input-field text-sm" placeholder="Account holder name"/></div>
                </div>
              </div>
              {/* Bank */}
              <div>
                <p className="text-xs font-black text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#1a3a6e] rounded-full"></span>Bank Transfer
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[{label:'Bank Name',key:'bankName',ph:'Meezan Bank'},{label:'Account Title',key:'accountTitle',ph:'SitSmart Store'},{label:'Account Number',key:'accountNo',ph:'00000000000'},{label:'IBAN',key:'iban',ph:'PK00BANK0000000000000'}].map(({label,key,ph}) => (
                    <div key={key}><label className="block text-xs text-gray-500 mb-1">{label}</label>
                      <input value={shipping.paymentAccounts?.bank?.[key]||''} onChange={e => updatePayAcc('bank',key,e.target.value)} className="input-field text-sm" placeholder={ph}/></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <button onClick={handleSaveShipping} disabled={saving} className="btn-primary text-sm flex items-center gap-2">
            {saving && <Spinner size="sm" color="white"/>} Save Shipping & Payment
          </button>
        </div>
      )}
    </div>
  );
};
export default AdminSettings;
